import { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { themeClasses, OptimizedImage, preloadImage } from '@member-portal/ui';

export interface SlideshowImage {
  id: string;
  url: string;
  description?: string;
  displayOrder?: number;
}

interface ImageSlideshowProps {
  images: SlideshowImage[];
  alt?: string;
}

function ImageSlideshow({ images, alt = 'Image' }: ImageSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  const hasMultipleImages = images.length > 1;

  // Scroll selected thumbnail into view (only after user interaction, not on mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (hasMultipleImages && thumbnailRefs.current[currentIndex] && thumbnailContainerRef.current) {
      // Use scrollLeft on the container instead of scrollIntoView to avoid page scroll
      const container = thumbnailContainerRef.current;
      const thumbnail = thumbnailRefs.current[currentIndex];
      if (thumbnail) {
        const containerWidth = container.offsetWidth;
        const thumbnailLeft = thumbnail.offsetLeft;
        const thumbnailWidth = thumbnail.offsetWidth;
        const scrollLeft = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [currentIndex, hasMultipleImages]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  if (images.length === 0) {
    return (
      <div className="relative aspect-[16/9] w-full bg-theme-bg-secondary">
        <div className="w-full h-full flex items-center justify-center">
          <div className={themeClasses.common.placeholderGrid}>
            <div className={themeClasses.common.placeholderItem}></div>
            <div className={themeClasses.common.placeholderItem}></div>
            <div className={themeClasses.common.placeholderItem}></div>
            <div className={themeClasses.common.placeholderItem}></div>
          </div>
        </div>
      </div>
    );
  }

  // Preload adjacent images when index changes
  useEffect(() => {
    if (hasMultipleImages) {
      const nextUrl = images[(currentIndex + 1) % images.length]?.url;
      const prevUrl = images[(currentIndex - 1 + images.length) % images.length]?.url;
      if (nextUrl) preloadImage(nextUrl);
      if (prevUrl) preloadImage(prevUrl);
    }
  }, [currentIndex, images, hasMultipleImages]);

  return (
    <div className="w-full">
      {/* Main Image */}
      <div className="relative aspect-[16/9] w-full bg-theme-bg-secondary">
        <OptimizedImage
          key={currentIndex}
          src={images[currentIndex]?.url}
          alt={images[currentIndex]?.description || alt}
          className="w-full h-full object-cover"
          quality={100}
          priority
        />

        {/* Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {hasMultipleImages && (
          <div className={`absolute top-4 right-4 px-3 py-1.5 bg-black/50 text-white ${themeClasses.text.mutedSmall}`}>
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultipleImages && (
        <div ref={thumbnailContainerRef} className="mt-3 overflow-x-auto">
          <div className="flex gap-2">
            {images.map((image, index) => (
              <button
                key={image.id}
                ref={(el) => { thumbnailRefs.current[index] = el; }}
                onClick={() => goToImage(index)}
                className={`relative flex-shrink-0 w-20 h-14 overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-2 ring-theme-accent opacity-100'
                    : 'opacity-60 hover:opacity-100'
                }`}
                aria-label={`View image ${index + 1}`}
              >
                <OptimizedImage
                  src={image.url}
                  alt={image.description || `Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  quality={60}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageSlideshow;
